import React, { useMemo } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { DateTime } from 'luxon';
import { FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';
import classnames from 'classnames';
import { LabelsFormFieldContextProvider } from 'modules/timesheet-labels';
import { useCollatedTimesheetLabels } from 'modules/timesheet-labels/hooks';
import {
  useIsBreakpointDown,
  useLabels,
  useDialogCancelAction
} from 'modules/common/hooks';
import { TimeSignal } from 'graphql/types';
import { FormTimesheetLabel } from 'modules/timesheet-labels/labelSchema';
import { useCollatedCustomLabels } from 'modules/customLabels/hooks/useCollatedCustomLabels';
import { useTimeSignalCustomLabels } from 'modules/customLabels/hooks/useTimeSignalCustomLabels';
import {
  NoteBody,
  NoteFooter,
  NoteHeader,
  TimerContextProvider,
  FormErrorDialogContextProvider,
  AttributesContent
} from 'modules/notes/common';
import { useScrollbarStyles } from 'modules/common/styles';
import { CustomLabels } from 'modules/customLabels/components/CustomLabels';
import { NoteContextProvider } from '../NoteContext';
import { useNotesToastContext } from '../NotesToastContextProvider';
import { NOTE_FIELDS } from '../common/noteSchema';
import {
  useNoteForm,
  useTimeSignalAttributes,
  useUpdateTimeSignal,
  useFormChangeHandlers,
  useNoteFormInitialValue
} from './hooks';
import TimesheetLabelsContent from './TimesheetLabelsContent';
import { useDialogStyles } from './AddNoteDialog';

const useStyles = makeStyles((theme) => ({
  title: {
    padding: theme.spacing(2, 2.5, 1, 1.5),
    color: theme.palette.grey[700],
    display: 'flex',
    justifyContent: 'space-between'
  },
  content: {
    padding: 0
  },
  footer: {
    padding: theme.spacing(1, 2.5)
  },
  moreBtn: {
    marginLeft: `${theme.spacing(2.5)} !important`
  }
}));

type EditNoteDialogProps = {
  open: boolean;
  onClose: () => void;
  timeSignal: TimeSignal;
};
export const EditNoteDialog: React.FC<EditNoteDialogProps> = ({
  open,
  onClose,
  timeSignal
}: EditNoteDialogProps) => {
  const { attributes } = useTimeSignalAttributes(timeSignal?.id);
  const classes = useStyles();
  const classesScrollbar = useScrollbarStyles();
  const noteDate = useMemo(() => DateTime.fromISO(timeSignal?.date), [
    timeSignal?.date
  ]);

  const {
    timesheetLabels,
    customLabels,
    showStartEndTime,
    loading: labelLoading
  } = useLabels(noteDate);

  const collatedTimesheetLabels = useCollatedTimesheetLabels(
    timeSignal,
    timesheetLabels
  );
  const collatedCustomLabels = useCollatedCustomLabels(
    timeSignal,
    customLabels
  );

  const initialFormData = useNoteFormInitialValue({
    customLabels: collatedCustomLabels,
    timesheetLabels: collatedTimesheetLabels,
    timeSignal
  });
  const {
    formMethods,
    formMethods: { register, handleSubmit },
    errorMessage,
    isValid,
    isFormDirty,
    timesheetLabelFields
  } = useNoteForm({
    timesheetLabels: collatedTimesheetLabels,
    initialValues: initialFormData,
    isEditDialog: true
  });
  const formChangeHandlers = useFormChangeHandlers(formMethods);

  const { setToastContext } = useNotesToastContext();

  const { onEditNote, loading } = useUpdateTimeSignal({
    onClose,
    showToastWithContext: setToastContext,
    initialLabelValue: useTimeSignalCustomLabels(timeSignal, customLabels)
  });

  const cancelActionProps = useDialogCancelAction({
    onClose,
    isFormDirty,
    isValidForm: isValid,
    loading
  });
  const { onNoteDiscard } = cancelActionProps;
  const { formatMessage } = useIntl();

  const fullScreen = useIsBreakpointDown('xs');

  return (
    <FormProvider {...formMethods}>
      <FormErrorDialogContextProvider>
        <NoteContextProvider date={noteDate}>
          <TimerContextProvider>
            <Dialog
              data-testid="edit-note-dialog"
              classes={useDialogStyles()}
              open={open}
              onClose={onNoteDiscard}
              fullScreen={fullScreen}
            >
              <DialogTitle
                className={classes.title}
                data-testid="edit-note-dialog-title"
              >
                <NoteHeader
                  title={formatMessage({ id: 'note.editNoteTitle' })}
                  timeSignal={timeSignal}
                />
              </DialogTitle>
              <DialogContent
                className={classnames(
                  classes.content,
                  classesScrollbar.scrollbar
                )}
                data-testid="edit-note-dialog-content"
              >
                <input
                  data-testid="edit-note-hidden-id"
                  type="hidden"
                  {...register('id')}
                />
                <NoteBody
                  errorMessage={errorMessage}
                  formChangeHandlers={formChangeHandlers}
                  showStartEndTime={showStartEndTime}
                  attributes={attributes}
                  timeSignal={timeSignal}
                  disableDate
                />
                <CustomLabels fieldName={NOTE_FIELDS.CUSTOM_LABELS} />
                <AttributesContent
                  attributes={attributes}
                  moreClassName={classes.moreBtn}
                />
                <LabelsFormFieldContextProvider
                  timesheetLabels={NOTE_FIELDS.TIMESHEET_LABELS}
                  note={NOTE_FIELDS.NOTE}
                >
                  <TimesheetLabelsContent
                    loading={labelLoading}
                    fields={timesheetLabelFields as FormTimesheetLabel[]}
                    attributes={attributes}
                  />
                </LabelsFormFieldContextProvider>
              </DialogContent>
              <DialogActions
                className={classes.footer}
                data-testid="edit-note-dialog-footer"
              >
                <NoteFooter
                  saveTitle={formatMessage({ id: 'note.editNote' })}
                  handleSubmit={handleSubmit}
                  onSubmitNote={onEditNote}
                  onSaveNoteWithTimerRunning={formChangeHandlers.handleDuration}
                  showStartEndTime={showStartEndTime}
                  loading={loading}
                  onClassificationChange={
                    formChangeHandlers.onChangeClassification
                  }
                  mode="edit"
                  classification={timeSignal.classification}
                  customLabels={NOTE_FIELDS.CUSTOM_LABELS}
                  timesheetLabels={NOTE_FIELDS.TIMESHEET_LABELS}
                  {...cancelActionProps}
                />
              </DialogActions>
            </Dialog>
          </TimerContextProvider>
        </NoteContextProvider>
      </FormErrorDialogContextProvider>
    </FormProvider>
  );
};
